import { useEffect, useId, useRef, useState, type InputHTMLAttributes } from 'react';

interface Props extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'min' | 'max' | 'type'
> {
  value: number;
  min: number;
  max: number;
  onValueChange: (value: number) => void;
  integer?: boolean;
  excludeZero?: boolean;
  wrapperClassName?: string;
}

/** One editable number, with an unfinished local draft and a finite bounded model value. */
export function StudyNumberInput({
  value,
  min,
  max,
  onValueChange,
  integer = false,
  excludeZero = false,
  wrapperClassName = 'block min-w-0',
  onBlur,
  onKeyDown,
  ...props
}: Props) {
  const [draft, setDraft] = useState(String(value));
  const [feedback, setFeedback] = useState('');
  const sent = useRef<number | null>(null);
  const feedbackId = useId();
  useEffect(() => {
    if (sent.current !== value) {
      setDraft(String(value));
      setFeedback('');
    }
    sent.current = null;
  }, [value]);
  const send = (next: number) => {
    if (next === value) return;
    sent.current = next;
    onValueChange(next);
  };
  const commit = () => {
    const parsed = draft.trim() === '' ? NaN : Number(draft);
    if (!Number.isFinite(parsed)) {
      setDraft(String(value));
      setFeedback(`Enter a number. Kept ${value}.`);
      return;
    }
    const bounded = Math.min(max, Math.max(min, integer ? Math.round(parsed) : parsed));
    if (excludeZero && bounded === 0) {
      setDraft(String(value));
      setFeedback(`Zero is not allowed here. Kept ${value}.`);
      return;
    }
    setDraft(String(bounded));
    setFeedback(
      bounded === parsed
        ? ''
        : `Used ${bounded}. ${integer ? 'Use whole numbers. ' : ''}Allowed range: ${min} to ${max}.`,
    );
    send(bounded);
  };
  const pending =
    draft.trim() === '' ||
    !Number.isFinite(Number(draft)) ||
    Number(draft) < min ||
    Number(draft) > max ||
    (integer && !Number.isInteger(Number(draft))) ||
    (excludeZero && Number(draft) === 0);
  return (
    <span className={wrapperClassName}>
      <input
        {...props}
        type="number"
        value={draft}
        min={min}
        max={max}
        aria-describedby={[props['aria-describedby'], feedbackId].filter(Boolean).join(' ')}
        onChange={(event) => {
          const text = event.target.value;
          const next = event.target.valueAsNumber;
          setDraft(text);
          setFeedback('');
          if (
            text !== '' &&
            Number.isFinite(next) &&
            next >= min &&
            next <= max &&
            (!integer || Number.isInteger(next)) &&
            (!excludeZero || next !== 0)
          )
            send(next);
        }}
        onBlur={(event) => {
          commit();
          onBlur?.(event);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
          onKeyDown?.(event);
        }}
      />
      <span
        id={feedbackId}
        role={feedback || pending ? 'status' : undefined}
        aria-live="polite"
        className="mt-1 block max-w-64 text-xs font-normal normal-case leading-5 tracking-normal text-ink-soft"
      >
        {feedback || (pending ? `Finish typing, then press Enter. Current value: ${value}.` : '')}
      </span>
    </span>
  );
}
